import { ofType } from 'redux-observable';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import * as StateManager from '../../store/state/state-manager';
import * as actions from '../../store/state/actions/action-creators';
import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { CONTAINER_NAMES, getTargetFromContainerItemID } from '../../container/container-utils';
import { getParentContextName } from '../../utils/context-utils';
import EnvironmentInfo from '../../environment-info';
import type { Action } from '../../store/state/state-manager';

import { executeSubmitBulkActionRequest } from './async-request';
import { publishBulkActionSubmitEvent } from './events';
import { SUBMIT_BULK_ACTION } from './action-creator';

interface BulkActionResponse {
  response: {
    successCount: number;
    failureCount: number;
    results: {
      status: string;
      error?: { errorDetails: { localizedValue: string }[] };
      BusinessID: string;
    }[];
  };
}

// Todo : Enhance after integrating with actual dxapi
export const submitBulkAction = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: Action[] = [];
  let actionInPayload: Action['payload'];
  let toastMessage = '';

  return action$.pipe(
    ofType(SUBMIT_BULK_ACTION),
    // eslint-disable-next-line sonarjs/cognitive-complexity
    switchMap((action: Action) => {
      const { isFormValid } = action.payload;
      ({ actionMgrID } = action.payload);

      if (!isFormValid) {
        const error = { type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR };
        ActionManagerInstance.rejectActionByID(actionMgrID, true, error);
        // return empty observable as form is not valid.
        return of([]);
      }
      return of(action.payload).pipe(
        mergeMap((payload) => {
          actionInPayload = payload;
          return executeSubmitBulkActionRequest(actionInPayload);
        }),
        mergeMap((chainedData: BulkActionResponse[]) => {
          let successCount = 0;
          let failureCount = 0;
          const errorDetails: { ID: string; message: string }[] = [];
          const { containerItemID, context } = actionInPayload;
          chainedData.forEach((data) => {
            const { response } = data;
            successCount += Number(response.successCount);
            failureCount += Number(response.failureCount);
            if (failureCount) {
              response.results.forEach((result) => {
                if (result.status !== '200' && result.error && result.error.errorDetails) {
                  result.error.errorDetails.forEach((errorObj) => {
                    errorDetails.push({ ID: result.BusinessID, message: errorObj.localizedValue });
                  });
                }
              });
            }
          });
          toastMessage = `${successCount} record(s) updated`;
          publishBulkActionSubmitEvent({ submitResponse: chainedData });
          actionInPayload.target =
            EnvironmentInfo.getRenderingMode() === 'HYBRID'
              ? getTargetFromContainerItemID(containerItemID)
              : `${constants.APP.APP}/${CONTAINER_NAMES.MODAL}`;
          actionsOut.push(
            actions.postProcess(actions.removeContainerItem, {
              containerItemID,
              context,
              target: actionInPayload.target,
              skipReleaseLockRequest: true
            })
          );
          if (failureCount) {
            actionsOut.push(
              actions.postProcess(actions.addMessages, {
                messages: [
                  {
                    type: 'error',
                    message:
                      failureCount === 1 ? '1 record failed to update' : `${failureCount} records failed to update`,
                    errorDetails,
                    showHeader: false
                  }
                ],
                context: getParentContextName(context) || context,
                category: 'HTTP',
                showHeader: false
              })
            );
          }
          return actionsOut;
        }),
        map(() => {
          ActionManagerInstance.resolveActionByID(actionMgrID, toastMessage);
          StateManager.dispatch(actionsOut);
          actionsOut = [];
          return actionsOut;
        })
      );
    })
  );
};

export default submitBulkAction;
